import React, { useEffect, useRef, useState } from 'react';
import { STORE_PATH_FUNCTION_EXECUTION } from '../../constants';
import {
	addListener,
	getDataFromPath,
	PageStoreExtractor,
	UrlDetailsExtractor,
} from '../../context/StoreContext';
import { runEvent } from '../util/runEvent';
import { HelperComponent } from '../HelperComponents/HelperComponent';
import { getTranslations } from '../util/getTranslations';
import { Component, ComponentPropertyDefinition, ComponentProps } from '../../types/common';
import { propertiesDefinition, stylePropertiesDefinition } from './buttonProperties';
import ButtonStyle from './ButtonStyle';
import useDefinition from '../util/useDefinition';
import { processComponentStylePseudoClasses } from '../../util/styleProcessor';
import { flattenUUID } from '../util/uuid';
import { getHref } from '../util/getHref';
import { isBrowserHandledLinkClick } from '../util/isBrowserHandledLinkClick';
import { useLocation, useNavigate } from 'react-router-dom';
import { SubHelperComponent } from '../HelperComponents/SubHelperComponent';
import { messageToMaster } from '../../slaveFunctions';
import { styleDefaults, stylePropertiesForTheme } from './buttonStyleProperties';
import { IconHelper } from '../util/IconHelper';
import getSrcUrl from '../util/getSrcUrl';
import { findPropertyDefinitions } from '../util/lazyStylePropertyUtil';
import { useComponentShortcut } from '../../shortcuts/useComponentShortcut';

function ButtonComponent(props: Readonly<ComponentProps>) {
	const pageExtractor = PageStoreExtractor.getForContext(props.context.pageName);
	const urlExtractor = UrlDetailsExtractor.getForContext(props.context.pageName);
	const [focus, setFocus] = useState(false);
	const [hover, setHover] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	let {
		key,
		properties: {
			label,
			onClick,
			colorScheme,
			designType,
			readOnly,
			leftIcon,
			rightIcon,
			leftImage,
			rightImage,
			activeLeftImage,
			activeRightImage,
			target,
			linkPath,
			stopPropagation,
			preventDefault,
			analyticsLabel,
			shortcutKey,
			shortcutScope,
			shortcutPriority,
			shortcutGroup,
		} = {},
		stylePropertiesWithPseudoStates,
	} = useDefinition(
		props.definition,
		propertiesDefinition,
		stylePropertiesDefinition,
		props.locationHistory,
		pageExtractor,
		urlExtractor,
	);

	const clickEvent = onClick ? props.pageDefinition.eventFunctions?.[onClick] : undefined;
	const spinnerPath = onClick
		? `${STORE_PATH_FUNCTION_EXECUTION}.${props.context.pageName}.${flattenUUID(
				onClick,
			)}.isRunning`
		: undefined;

	const [isLoading, setIsLoading] = useState(
		onClick
			? (getDataFromPath(spinnerPath, props.locationHistory, pageExtractor) ?? false)
			: false,
	);

	useEffect(() => {
		if (spinnerPath) {
			return addListener(
				props.context.pageName,
				(_, value) => setIsLoading(value),
				spinnerPath,
			);
		}
	}, []);

	// The running flag is kept per page and function, so every row of a list whose buttons
	// share one onClick saw it and all of them spun. A repeated button now shows the spinner
	// only for the run it started; the shared flag still blocks a second run from any row.
	const [startedHere, setStartedHere] = useState(false);
	const isRepeated = (props.locationHistory?.length ?? 0) > 0;
	const showSpinner = isLoading && (!isRepeated || startedHere);

	const styleProperties = processComponentStylePseudoClasses(
		props.pageDefinition,
		{ focus, hover, disabled: showSpinner || readOnly },
		stylePropertiesWithPseudoStates,
	);

	const eventRunningRef = useRef(false);

	const handleClick = async (e: any) => {
		if (stopPropagation) e.stopPropagation();
		if (preventDefault) e.preventDefault();
		// Cmd/Ctrl or Shift click on a button with a link opens the link elsewhere, as on an
		// anchor (Link, Grid): a new tab, or a new window with Shift. This page stays as it is
		// and the click event does not run. Keyboard activation is not a click, so it navigates.
		if (linkPath && e?.type === 'click' && isBrowserHandledLinkClick(e)) {
			window.open(getHref(linkPath, location), '_blank', e.shiftKey ? 'popup' : undefined);
			return;
		}
		if (linkPath) {
			if (target) {
				window.open(getHref(linkPath, location), target);
			} else {
				if (
					linkPath?.startsWith('http:') ||
					linkPath?.startsWith('https:') ||
					linkPath?.startsWith('//') ||
					linkPath?.startsWith('www')
				)
					window.location.href = linkPath;
				else navigate(getHref(linkPath, location)!);
			}
		}

		if (clickEvent && !isLoading && !eventRunningRef.current) {
			eventRunningRef.current = true;
			setStartedHere(true);
			try {
				await runEvent(
					clickEvent,
					onClick,
					props.context.pageName,
					props.locationHistory,
					props.pageDefinition,
				);
			} finally {
				eventRunningRef.current = false;
				setStartedHere(false);
			}
		}
	};

	const buttonRef = useRef<HTMLButtonElement>(null);

	const hasRightIcon = !leftIcon && !leftImage && (rightIcon || rightImage);

	// handleClick already guards against a double run through eventRunningRef and
	// isLoading, and it carries the linkPath navigation, so the keyboard path gets
	// both for free.
	const {
		aria: shortcutAria,
		titleSuffix: shortcutTooltip,
		hint: shortcutHint,
	} = useComponentShortcut({
		props,
		componentKey: key,
		shortcutKey,
		shortcutScope,
		shortcutPriority,
		shortcutGroup,
		label,
		fallbackLabel: 'Button',
		disabled: showSpinner || readOnly,
		elementRef: buttonRef,
		onActivate: e => handleClick(e),
	});

	const hasRightIconClass = (!!rightIcon || !!rightImage) && !leftIcon;
	const hasLabel =
		!designType.startsWith('_fabButton') &&
		designType !== '_iconButton' &&
		designType !== '_iconPrimaryButton';

	let rightIconTag = undefined;

	if (rightImage) {
		rightIconTag = showSpinner ? (
			<i className="fa fa-circle-notch fa-spin _rightButtonIcon _icon"></i>
		) : (
			<img
				src={getSrcUrl(hover && activeRightImage ? activeRightImage : rightImage)}
				alt="right"
				style={
					(hover
						? (styleProperties.activeRightImage ?? styleProperties.rightImage)
						: styleProperties.rightImage) ?? {}
				}
				className={hover ? '_rightButtonActiveImage' : '_rightButtonImage'}
			/>
		);
	} else if (hasLabel && rightIcon) {
		// No icon, no tag. The old `fa-circle-notch hide` stand-in never showed a
		// spinner (`.hide` kept it at opacity 0 even while loading), but it still took
		// the icon's width and margin, so every icon-less button was wider than its
		// padding said.
		rightIconTag = (
			<i
				style={styleProperties.rightIcon ?? {}}
				className={`_rightButtonIcon _icon ${
					!showSpinner ? rightIcon : 'fa fa-circle-notch fa-spin'
				}`}
			>
				<SubHelperComponent
					definition={props.definition}
					subComponentName="rightIcon"
				></SubHelperComponent>
			</i>
		);
	}

	const hasLeftIcon = leftIcon || leftImage || showSpinner;

	let leftIconTag = undefined;
	if (leftImage) {
		leftIconTag = showSpinner ? (
			<i className="fa fa-circle-notch fa-spin _leftButtonIcon _icon"></i>
		) : (
			<img
				src={getSrcUrl(hover && activeLeftImage ? activeLeftImage : leftImage)}
				alt="left"
				style={
					(hover
						? (styleProperties.activeLeftImage ?? styleProperties.leftImage)
						: styleProperties.leftImage) ?? {}
				}
				className={hover ? '_leftButtonActiveImage' : '_leftButtonImage'}
			/>
		);
	} else if (leftIcon) {
		leftIconTag = (
			<i
				style={styleProperties.leftIcon ?? {}}
				className={`_leftButtonIcon _icon ${
					!showSpinner ? leftIcon : 'fa fa-circle-notch fa-spin'
				}`}
			>
				<SubHelperComponent
					definition={props.definition}
					subComponentName="leftIcon"
				></SubHelperComponent>
			</i>
		);
	}
	const [editableLabel, setEditableLabel] = useState(label);
	const [editName, setEditName] = useState(false);
	useEffect(() => setEditableLabel(label), [label]);
	label = getTranslations(label, props.pageDefinition.translations);

	let buttonBar = undefined;

	if (globalThis.isDesignMode) {
		buttonBar = (
			<>
				{editName && (
					<>
						<input
							type="text"
							className="nameEditor compButton"
							style={styleProperties.comp ?? {}}
							value={editableLabel}
							onKeyDown={ev => {
								if (ev.key === 'Enter') {
									ev.preventDefault();
									ev.stopPropagation();
									setEditName(false);
									messageToMaster({
										type: 'SLAVE_COMP_PROP_CHANGED',
										payload: {
											key: props.definition.key,
											properties: [{ name: 'label', value: editableLabel }],
										},
									});
								} else if (ev.key === 'Escape') {
									ev.preventDefault();
									ev.stopPropagation();
									setEditName(false);
									setEditableLabel(label);
								}
							}}
							onKeyUp={ev => {
								if (ev.key === ' ') {
									ev.preventDefault();
									ev.stopPropagation();
								}
							}}
							onChange={ev => {
								if (!ev.target) return;
								setEditableLabel(ev.target.value);
							}}
							onBlur={() => {
								setEditName(false);
								messageToMaster({
									type: 'SLAVE_COMP_PROP_CHANGED',
									payload: {
										key: props.definition.key,
										properties: [{ name: 'label', value: editableLabel }],
									},
								});
							}}
							autoFocus={true}
							onClick={e => {
								e.preventDefault();
								e.stopPropagation();
							}}
							onMouseDown={e => {
								e.stopPropagation();
							}}
							onMouseUp={e => {
								e.stopPropagation();
							}}
						/>
					</>
				)}
				<div
					className="textToolBar"
					onClick={e => {
						e.preventDefault();
						e.stopPropagation();
					}}
					onMouseDown={e => {
						e.preventDefault();
						e.stopPropagation();
					}}
					onMouseUp={e => {
						e.preventDefault();
						e.stopPropagation();
					}}
				>
					<i
						className="fa fa-solid fa-bold"
						onClick={() => {
							messageToMaster({
								type: 'SLAVE_COMP_PROP_CHANGED',
								payload: {
									key: props.definition.key,
									styleProperties: [
										{ name: 'fontWeight', value: 'bold', strategy: 'toggle' },
									],
								},
							});
						}}
					/>
					<i
						className="fa fa-solid fa-italic"
						onClick={() => {
							messageToMaster({
								type: 'SLAVE_COMP_PROP_CHANGED',
								payload: {
									key: props.definition.key,
									styleProperties: [
										{ name: 'fontStyle', value: 'italic', strategy: 'toggle' },
									],
								},
							});
						}}
					/>
					<i
						className="fa fa-solid fa-strikethrough"
						onClick={() => {
							messageToMaster({
								type: 'SLAVE_COMP_PROP_CHANGED',
								payload: {
									key: props.definition.key,
									styleProperties: [
										{
											name: 'textDecoration',
											value: 'line-through',
											strategy: 'toggle',
										},
									],
								},
							});
						}}
					/>
					<i
						className="fa fa-solid fa-rotate-180 fa-underline"
						onClick={() => {
							messageToMaster({
								type: 'SLAVE_COMP_PROP_CHANGED',
								payload: {
									key: props.definition.key,
									styleProperties: [
										{
											name: 'textDecoration',
											value: 'overline',
											strategy: 'toggle',
										},
									],
								},
							});
						}}
					/>
					<i
						className="fa fa-solid fa-underline"
						onClick={() => {
							messageToMaster({
								type: 'SLAVE_COMP_PROP_CHANGED',
								payload: {
									key: props.definition.key,
									styleProperties: [
										{
											name: 'textDecoration',
											value: 'underline',
											strategy: 'toggle',
										},
									],
								},
							});
						}}
					/>
					<div className="colorPicker">
						<i className="fa fa-solid fa-palette" />
						<input
							type="color"
							onClick={e => {
								e.stopPropagation();
							}}
							onChange={e => {
								messageToMaster({
									type: 'SLAVE_COMP_PROP_CHANGED',
									payload: {
										key: props.definition.key,
										styleProperties: [
											{
												name: 'color',
												value: e.target.value,
											},
										],
									},
								});
							}}
						/>
					</div>
					<div className="colorPicker">
						<i className="fa fa-solid fa-fill-drip" />
						<input
							type="color"
							onClick={e => {
								e.stopPropagation();
							}}
							onChange={e => {
								messageToMaster({
									type: 'SLAVE_COMP_PROP_CHANGED',
									payload: {
										key: props.definition.key,
										styleProperties: [
											{
												name: 'backgroundColor',
												value: e.target.value,
											},
										],
									},
								});
							}}
						/>
					</div>
				</div>
			</>
		);
	}

	return (
		<button
			className={`comp compButton button ${designType} ${colorScheme} ${
				hasLeftIcon ? '_withLeftIcon' : ''
			} ${hasRightIcon ? '_withRightIcon' : ''}`}
			disabled={showSpinner || readOnly}
			onClick={handleClick}
			style={styleProperties.comp ?? {}}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			ref={buttonRef}
			onFocus={stylePropertiesWithPseudoStates?.focus ? () => setFocus(true) : undefined}
			onBlur={stylePropertiesWithPseudoStates?.focus ? () => setFocus(false) : undefined}
			title={`${label ?? ''}${shortcutTooltip ?? ''}`}
			aria-keyshortcuts={shortcutAria}
			data-analytics-label={analyticsLabel || undefined}
		>
			<HelperComponent
				context={props.context}
				definition={props.definition}
				onDoubleClick={() => setEditName(true)}
			>
				{buttonBar}
			</HelperComponent>
			{leftIconTag}
			{!designType?.startsWith('_fabButton') && designType !== '_iconButton' ? label : ''}
			{/* An icon only button is 32px square, with no room for a chip. Its key
			    still shows in the cheat sheet on ?. */}
			{hasLabel ? shortcutHint?.(styleProperties.shortcutHint) : undefined}
			{rightIconTag}
		</button>
	);
}

const { designType, colorScheme } = findPropertyDefinitions(
	propertiesDefinition,
	'designType',
	'colorScheme',
);

const component: Component = {
	order: 4,
	name: 'Button',
	displayName: 'Button',
	description: 'Button component',
	component: ButtonComponent,
	styleComponent: ButtonStyle,
	styleDefaults: styleDefaults,
	propertyValidation: (props: ComponentPropertyDefinition): Array<string> => [],
	properties: propertiesDefinition,
	styleProperties: stylePropertiesDefinition,
	stylePseudoStates: ['focus', 'hover', 'disabled'],
	defaultTemplate: {
		key: '',
		name: 'button',
		type: 'Button',
		properties: {
			label: { value: 'Button' },
		},
	},
	sections: [{ name: 'Buttons', pageName: 'buttons' }],
	propertiesForTheme: [designType, colorScheme],
	stylePropertiesForTheme: stylePropertiesForTheme,
	externalStylePropsForThemeJson: true,
};

export default component;
